// ══════════════════════════════════════════════════════════════════════════
// Parser GS1 — Extrait les données d'un datamatrix pharmaceutique
// AI 01 (GTIN 14 chars) → CIP13
// AI 17 (6 chars)       → Date de péremption AAMMJJ
// AI 10 (variable)      → Lot
// AI 21 (variable)      → Série unitaire
// Séparateur : GS = \u001d (fin des AI variables)
// ══════════════════════════════════════════════════════════════════════════

const GS_CHAR = '\u001d';

const FIXED_AI = {
  '00': 18, '01': 14, '02': 14, '11': 6, '13': 6, '15': 6, '17': 6, '20': 2, '31': 10,
};

const VARIABLE_AI = new Set(['10', '21', '22', '240', '241', '250', '251', '30', '37', '90', '91']);

function looksLikeGS1(str) {
  if (!str || str.length < 4) return false;
  // Strip AIM identifiers (various formats from different scanners/polyfills)
  const cleaned = str.replace(/^(\]d[0-9]|\]C[0-9]|\]Q[0-9]|\]e[0-9]|\]E[0-9])/, '');
  const firstAI = cleaned.substring(0, 2);
  return firstAI === '01' || firstAI === '00' || firstAI === '17' || firstAI === '10';
}

export function parseGS1(raw) {
  if (!raw) return { isGS1: false, raw: '' };
  let str = String(raw).replace(/^(\]d[0-9]|\]C[0-9]|\]Q[0-9]|\]e[0-9]|\]E[0-9])/, '');
  if (!looksLikeGS1(str)) return { isGS1: false, raw };

  const result = { isGS1: true, raw, ais: {} };
  let pos = 0;

  while (pos < str.length) {
    if (str[pos] === GS_CHAR) { pos++; continue; }
    let ai = str.substring(pos, pos + 2);

    if (FIXED_AI[ai]) {
      const len = FIXED_AI[ai];
      result.ais[ai] = str.substring(pos + 2, pos + 2 + len);
      pos += 2 + len;
    } else if (VARIABLE_AI.has(ai)) {
      const start = pos + 2;
      let end = str.indexOf(GS_CHAR, start);
      if (end === -1) end = str.length;
      result.ais[ai] = str.substring(start, end);
      pos = end;
    } else {
      const ai3 = str.substring(pos, pos + 3);
      if (VARIABLE_AI.has(ai3)) {
        const start = pos + 3;
        let end = str.indexOf(GS_CHAR, start);
        if (end === -1) end = str.length;
        result.ais[ai3] = str.substring(start, end);
        pos = end;
      } else {
        result.parseError = 'AI inconnu à position ' + pos + ': ' + ai;
        break;
      }
    }
  }

  if (result.ais['01']) {
    const gtin = result.ais['01'];
    if (gtin.length === 14 && /^\d{14}$/.test(gtin)) {
      result.cip13 = gtin.substring(1);
      result.gtin = gtin;
    }
  }
  if (result.ais['10']) result.lot = result.ais['10'];
  if (result.ais['17']) {
    const raw17 = result.ais['17'];
    if (raw17.length === 6 && /^\d{6}$/.test(raw17)) {
      const yy = parseInt(raw17.substring(0, 2), 10);
      const mm = raw17.substring(2, 4);
      const dd = raw17.substring(4, 6);
      const year = 2000 + yy;
      const day = (dd === '00') ? lastDayOfMonth(year, parseInt(mm, 10)) : dd;
      result.expirationRaw = raw17;
      result.expiration = `${year}-${mm}-${String(day).padStart(2, '0')}`;
    }
  }
  if (result.ais['21']) result.serial = result.ais['21'];
  return result;
}

function lastDayOfMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

export function parseScanned(raw) {
  const gs1 = parseGS1(raw);
  if (gs1.isGS1 && gs1.cip13) return gs1;
  
  const clean = String(raw || '').trim();
  
  // CIP13 exact
  if (/^\d{13}$/.test(clean)) {
    return { isGS1: false, raw, cip13: clean, gtin: null, lot: null, expiration: null, serial: null, fallback: 'cip13_only' };
  }
  
  // GTIN14 exact (0 + CIP13)
  if (/^\d{14}$/.test(clean)) {
    return { isGS1: false, raw, cip13: clean.substring(1), gtin: clean, lot: null, expiration: null, serial: null, fallback: 'gtin14' };
  }
  
  // Chercher AI 01 (GTIN) dans la chaîne brute — le polyfill peut ajouter des préfixes
  const ai01Match = clean.match(/01(\d{14})/);
  if (ai01Match) {
    const gtin = ai01Match[1];
    const cip13 = gtin.substring(1);
    // Re-tenter le parse GS1 à partir de la position trouvée
    const fromAI = clean.substring(clean.indexOf('01' + gtin));
    const gs1Retry = parseGS1(fromAI);
    if (gs1Retry.isGS1 && gs1Retry.cip13) return gs1Retry;
    return { isGS1: false, raw, cip13, gtin, lot: null, expiration: null, serial: null, fallback: 'ai01_found' };
  }
  
  // Chercher un CIP13 français (commence par 340) n'importe où
  const cipMatch = clean.match(/(340\d{10})/);
  if (cipMatch) {
    return { isGS1: false, raw, cip13: cipMatch[1], gtin: null, lot: null, expiration: null, serial: null, fallback: 'cip_regex' };
  }

  return { isGS1: false, raw, fallback: 'unknown' };
}

export function isShortExpiry(expirationIso, thresholdMonths = 6) {
  if (!expirationIso) return false;
  const exp = new Date(expirationIso);
  const threshold = new Date();
  threshold.setMonth(threshold.getMonth() + thresholdMonths);
  return exp < threshold;
}
